import { useEffect, useState } from "react";

import { ApiError, apiClient, type CatalogContexts, type CatalogProductPage } from "../api/client";
import {
  AddProductDialog,
  CsvImportDialog,
  DeleteProductsDialog,
  ImageImportDialog,
} from "./product-catalog/ProductDialogs";
import { ProductTable } from "./product-catalog/ProductTable";
import { useAuth } from "../app/AuthContext";

type DialogName = "add" | "csv" | "images" | "delete" | null;
const EMPTY_PAGE: CatalogProductPage = { items: [], total: 0, page: 1, page_size: 20 };
const EMPTY_CONTEXTS: CatalogContexts = { stores: [], responsible_users: [] };

export function ProductCatalogPage() {
  const { session } = useAuth();
  const [contexts, setContexts] = useState(EMPTY_CONTEXTS);
  const [storeId, setStoreId] = useState("");
  const [responsibleUserId, setResponsibleUserId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<20 | 50 | 100>(20);
  const [products, setProducts] = useState(EMPTY_PAGE);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<DialogName>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    void apiClient
      .getCatalogContexts()
      .then((resolved) => {
        if (!active) return;
        setContexts(resolved);
        setStoreId((current) => current || resolved.stores[0]?.id || "");
      })
      .catch((caught) => active && setError(message(caught, "无法加载商品授权范围")))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!storeId) return;
    let active = true;
    void apiClient
      .getCatalogContexts(storeId)
      .then((resolved) => {
        if (!active) return;
        setContexts(resolved);
        setResponsibleUserId((current) =>
          resolved.responsible_users.some((user) => user.id === current)
            ? current
            : resolved.responsible_users[0]?.id || "",
        );
      })
      .catch((caught) => active && setError(message(caught, "无法加载负责人范围")));
    return () => {
      active = false;
    };
  }, [storeId]);

  useEffect(() => {
    if (!storeId || !responsibleUserId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void apiClient
      .listCatalogProducts({
        storeId,
        responsibleUserId,
        page,
        pageSize,
        signal: controller.signal,
      })
      .then((resolved) => {
        setProducts(resolved);
        setSelected(new Set());
      })
      .catch((caught) => {
        if (!controller.signal.aborted) setError(message(caught, "无法加载商品列表"));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [page, pageSize, refresh, responsibleUserId, storeId]);

  const pageCount = Math.max(1, Math.ceil(products.total / pageSize));
  const readOnly = Boolean(
    responsibleUserId && session?.user_id && responsibleUserId !== session.user_id,
  );

  function complete(action: string) {
    setDialog(null);
    setNotice(action);
    setPage(1);
    setRefresh((value) => value + 1);
  }

  async function exportCsv() {
    if (!storeId) return;
    setError(null);
    try {
      const blob = await apiClient.exportCatalogProducts(storeId, responsibleUserId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "products.csv";
      anchor.click();
      URL.revokeObjectURL(url);
      setNotice("CSV 已导出");
    } catch (caught) {
      setError(message(caught, "CSV 导出失败"));
    }
  }

  if (!loading && contexts.stores.length === 0) {
    return (
      <section className="empty-page">
        <span className="eyebrow">商品目录</span>
        <h1>暂无可访问店铺</h1>
        <p>请联系管理员为当前账户授予店铺范围后再管理商品。</p>
      </section>
    );
  }

  return (
    <div className="catalog-page">
      <CatalogPageHeader total={products.total} />

      <section className="catalog-control-panel" aria-label="商品筛选与操作">
        <div className="catalog-scope-filters">
          <label>
            <span>店铺</span>
            <select
              disabled={contexts.stores.length <= 1}
              onChange={(event) => {
                setStoreId(event.target.value);
                setPage(1);
              }}
              value={storeId}
            >
              {contexts.stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>负责人</span>
            <select
              disabled={contexts.responsible_users.length <= 1}
              onChange={(event) => {
                setResponsibleUserId(event.target.value);
                setPage(1);
              }}
              value={responsibleUserId}
            >
              {contexts.responsible_users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.display_name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="catalog-actions">
          <button
            className="primary"
            disabled={!storeId || readOnly}
            onClick={() => setDialog("add")}
          >
            新增商品
          </button>
          <button disabled={!storeId || readOnly} onClick={() => setDialog("csv")}>
            CSV 导入
          </button>
          <button disabled={!storeId} onClick={() => void exportCsv()}>
            导出 CSV
          </button>
          <button disabled={!storeId || readOnly} onClick={() => setDialog("images")}>
            导入商品图片
          </button>
          <button
            className="danger"
            disabled={selected.size === 0 || readOnly}
            onClick={() => setDialog("delete")}
          >
            删除所选
          </button>
        </div>
      </section>

      {(error || notice) && (
        <div className={error ? "catalog-banner error" : "catalog-banner success"} role="status">
          {error || notice}
        </div>
      )}
      {readOnly && (
        <div className="catalog-banner readonly" role="status">
          当前正在查看他人负责的商品，仅可浏览和导出 CSV。
        </div>
      )}

      <ProductTable
        loading={loading}
        onSelectionChange={setSelected}
        products={products.items}
        selectable={!readOnly}
        selected={selected}
      />

      <footer className="catalog-pagination">
        <span>
          第 {Math.min(page, pageCount)} / {pageCount} 页，共 {products.total} 条
        </span>
        <label>
          <span>每页</span>
          <select
            aria-label="每页展示条数"
            onChange={(event) => {
              setPageSize(Number(event.target.value) as 20 | 50 | 100);
              setPage(1);
            }}
            value={pageSize}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <div>
          <button disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>
            上一页
          </button>
          <button
            disabled={page >= pageCount || loading}
            onClick={() => setPage((value) => value + 1)}
          >
            下一页
          </button>
        </div>
      </footer>

      <CatalogDialogLayer
        dialog={dialog}
        onClose={() => setDialog(null)}
        onComplete={complete}
        onImagesComplete={() => {
          setNotice("商品图片导入已处理");
          setRefresh((value) => value + 1);
        }}
        productIds={[...selected]}
        storeId={storeId}
      />
    </div>
  );
}

function CatalogPageHeader({ total }: { total: number }) {
  return (
    <header className="catalog-page-heading">
      <div>
        <span className="eyebrow">商品目录</span>
        <h1>商品管理</h1>
        <p>维护当前负责人商品，批量导入基础信息和商品图片。</p>
      </div>
      <div className="catalog-count" aria-label={`共 ${total} 条商品`}>
        <strong>{total}</strong>
        <span>条商品</span>
      </div>
    </header>
  );
}

function CatalogDialogLayer({
  dialog,
  storeId,
  productIds,
  onClose,
  onComplete,
  onImagesComplete,
}: {
  dialog: DialogName;
  storeId: string;
  productIds: string[];
  onClose: () => void;
  onComplete: (notice: string) => void;
  onImagesComplete: () => void;
}) {
  if (dialog === "add") {
    return (
      <AddProductDialog
        onClose={onClose}
        onComplete={() => onComplete("商品已保存")}
        storeId={storeId}
      />
    );
  }
  if (dialog === "csv") {
    return (
      <CsvImportDialog
        onClose={onClose}
        onComplete={() => onComplete("CSV 已成功导入")}
        storeId={storeId}
      />
    );
  }
  if (dialog === "images") {
    return <ImageImportDialog onClose={onClose} onComplete={onImagesComplete} storeId={storeId} />;
  }
  if (dialog === "delete") {
    return (
      <DeleteProductsDialog
        onClose={onClose}
        onComplete={() => onComplete("所选商品已删除")}
        productIds={productIds}
        storeId={storeId}
      />
    );
  }
  return null;
}

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}
